import React, { useEffect, useState } from 'react';
import { challengesAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import HLSPlayer from '../../components/HLSPlayer';
import { motion } from 'framer-motion';
import { Trophy, MessageSquare, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function MyChallenges() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await challengesAPI.mySubmissions();
        setSubs(res.data || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  return (
    <Layout title="MY CHALLENGES" showBack>
      <div className="px-4 py-6 space-y-4">
        <div>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" data-testid="my-challenges-title">MY CHALLENGE SUBMISSIONS</h2>
          <p className="text-xs text-[#64748B] mt-1">{subs.length} submission{subs.length !== 1 ? 's' : ''} — newest first</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : subs.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <Trophy size={32} className="mx-auto text-[#CBD5E1] mb-2" />
            <p className="text-[#64748B] text-sm">You haven't submitted any challenges yet.</p>
            <p className="text-[#94A3B8] text-xs mt-1">Once a challenge is live on your Home screen, tap Upload Your Challenge Video to enter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s, i) => {
              const reviewed = s.status === 'reviewed';
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm"
                  data-testid={`my-challenge-${i}`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-2 bg-[#FF9100]/10 border-b border-[#FF9100]/20">
                    <div className="flex items-center gap-2">
                      <Trophy size={14} className="text-[#FF9100]" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9100]">
                        Week {s.challenge_week}
                      </span>
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1 ${
                      reviewed ? 'bg-[#10B981] text-white' : 'bg-[#2563EB] text-white'
                    }`}>
                      {reviewed ? <><CheckCircle2 size={9} /> Reviewed</> : <><Clock size={9} /> Pending</>}
                    </span>
                  </div>

                  {/* Video */}
                  {s.video_filename && (
                    <HLSPlayer
                      videoFilename={s.video_filename}
                      watermarkText={user?.name || user?.email}
                      className="w-full aspect-video bg-black"
                    />
                  )}

                  {/* Body */}
                  <div className="px-4 py-3 space-y-2">
                    <h4 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight text-[#0A2242]">{s.challenge_title}</h4>
                    <p className="text-[10px] text-[#94A3B8]">
                      Submitted {new Date(s.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {s.reviewed_at && <> · Reviewed {new Date(s.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>}
                    </p>
                    {s.notes && (
                      <div className="bg-[#F1F5F9] rounded-lg p-2.5">
                        <p className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold mb-0.5">Your Note</p>
                        <p className="text-xs text-[#0A2242]">"{s.notes}"</p>
                      </div>
                    )}
                    {reviewed && s.coach_response && (
                      <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <MessageSquare size={11} className="text-[#10B981]" />
                          <p className="text-[9px] uppercase tracking-widest text-[#10B981] font-bold">Coach Feedback</p>
                        </div>
                        <p className="text-xs text-[#0A2242]">{s.coach_response}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
